<script>
  import { createEventDispatcher, onMount, onDestroy } from 'svelte';
  import Modal from '@components/Modal.svelte';
  import AccountVerifiedIcon from '@app-components/AccountVerifiedIcon.svelte';
  import CreatorSpotlightAjax from '@ajax/creator_spotlight';
  import Avatar from '@components/Avatar.svelte';
  import UserFollowButton from '@app-components/UserFollowButton.svelte';
  import Button from '@components/Button.svelte';
  import Text from '@components/Text.svelte';
  import { getCurrentUserStore } from '@stores/current_user';
  import Carousel from '@app-components/modal/creator_spotlight/Carousel.svelte';
  import { getProfileStore, initializeProfileStores } from '@stores/profile';
  import PinnedList from '@app-components/modal/creator_spotlight/PinnedList.svelte';
  import EditCreatorSpotlightModal from '@app-components/modal/EditCreatorSpotlightModal.svelte';
  import PreloadBox from '@components/PreloadBox.svelte';
  import { handleHrefLink } from '../../utils/link-helpers';
  import Track from '@utils/track';

  initializeProfileStores();
  let currentUser = getCurrentUserStore('default');

  const dispatch = createEventDispatcher();

  export let user;

  export let mobile = false;
  export let showModal = false;
  export let clickContext = 'profile_photo';
  let groupsStore = getProfileStore('groupsStore');
  let topThreeFollowers = [];
  let otherFollowersCount = 0;
  let invitedBy = null;
  let dataLoaded = false;
  let basicInformation = null;
  let socialProfiles = [];
  let showCreatorSpotlightEditModal = false;

  let values = [];
  let brands = [];
  let expertises = [];
  let questions = [];
  let interests = [];
  let galleryItems = [];
  let pinnedLists = [];

  const loadSpotlightInformation = async function () {
    let spotlightInformation = await CreatorSpotlightAjax.getCreatorSpotlightInformation(user);
    invitedBy = spotlightInformation.invited_by_user;
    basicInformation = spotlightInformation.basic_information;
    socialProfiles = spotlightInformation.social_profiles;
    values = spotlightInformation.values;
    brands = spotlightInformation.brands;
    expertises = spotlightInformation.expertises;
    questions = spotlightInformation.questions;
    interests = spotlightInformation.interests;
    pinnedLists = spotlightInformation.pinned_lists;
    groupsStore.set(pinnedLists);
    let followersInformation = spotlightInformation.followers_information;
    topThreeFollowers = followersInformation.top_three_followers;
    otherFollowersCount = followersInformation.followers_count;

    galleryItems = spotlightInformation.gallery_items.map((item) => ({
        ...item,
        content_url: item.content_type == 'image' 
        ? `https://image-proxy.benable.com/x/w:350/dpr:2/plain/${item.content_url}` : item.content_url,
      }
    ));

    dataLoaded = true;
  };

  loadSpotlightInformation();

  onMount(() => {
    Track.spotlightViewed({ currentUser: currentUser, user: user, path: location.pathname, buttonType: clickContext });
    document.body.classList.add('creator-spotlight-show');
  });

  onDestroy(() => {
    document.body.classList.remove('creator-spotlight-show');
  });
</script>

<style style="text/scss" lang="scss">
  * :global(.modal__container) {
    max-width: 860px !important;
  }

  * :global(.modal__header) {
    position: absolute;
    right: 20px;
    top: 20px;
  }

  * :global(.modal__content) {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  * :global(.activity-feed-item .owl-prev),
  * :global(.activity-feed-item .owl-next) {
    background-color: #fff !important;
    color: black !important;
    margin-top: 0 !important;
    transform: translateY(-100%);
  }

  * :global(.modal__close--custom) {
    transform: translate(14px, -16px);
  }

  .content-container {
    display: flex;
    gap: 24px;
  }

  .main-container {
    min-width: 350px;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 24px;
  }

  .side-container {
    display: flex;
    flex-direction: column;
    gap: 32px;
    padding-right: 8px;
    padding-left: 8px;
    max-height: 600px;
  }

  .image-carousel {
    position: relative;

    .edit-button {
      background: #ffffff;
      border-radius: 999px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      position: absolute;
      top: 0.5em;
      left: 0.5em;
      z-index: 2;

      &:hover {
        background: rgba(255, 255, 255, 0.5);
      }
    }
  }

  .spotlight-image {
    width: 100%;
    max-width: 350px;
    max-height: 350px;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    border-radius: 12px;
  }

  .user-meta,
  .preload-user-stack {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .name {
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
  }

  .username {
    font-size: 16px;
    color: #7f7f7f;
  }

  .user-stack {
    display: flex;
    font-size: 14px;
    color: #7f7f7f;
    gap: 8px;

    .user-stack__link {
      color: #000;
      display: inline-block;
    }
  }

  .user-stack-avatars {
    white-space: nowrap;
    .follower {
      margin-right: -12px;

      &:last-child {
        margin-right: 0;
      }
    }
  }

  .followers-text {
    font-size: 0;
    span,
    a {
      font-size: 14px;
    }
  }

  .user-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .user-details-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .user-details {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 16px;
    color: #7f7f7f;

    svg {
      min-width: 24px;
    }
  }

  .spotlight-heading {
    color: #1a1a1a;
    font-weight: 500;
    margin-bottom: 12px;
  }

  .pill-container {
    display: inline-flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .pill {
    border: 1px solid #e3e3e3;
    color: #1a1a1a;
    padding: 8px 16px;
    border-radius: 32px;
    font-weight: 500;
  }

  .answers-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .question-container {
    padding: 12px 20px;
    border: 1px solid var(--neutral-input-gray, #c6c6c6);
    border-radius: 12px;
  }

  .question-item {
    font-size: 14px;
    color: #717171;
  }

  .answer-item {
    font-size: 16px;
    color: #1c1c1c;
    font-weight: 500;
  }

  .pinned-list-container {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-bottom: 24px;
  }

  .social-icons {
    margin-top: 16px;
    a {
      svg {
        height: 24px;
        width: 24px;
      }
    }
  }

  .mobile {
    &.content-container {
      flex-direction: column;
      gap: 32px;
    }
    .main-container {
      min-width: unset !important;
    }

    .side-container {
      max-height: fit-content;
      padding: 0;
    }
  }

  * :global(.mobile .modal__container) {
    max-height: 88dvh;
  }
</style>

{#if showCreatorSpotlightEditModal}
  <EditCreatorSpotlightModal
    mobile={mobile}
    skipIntroStep={true}
    bind:showModal={showCreatorSpotlightEditModal}
    on:finish={() => {
      loadSpotlightInformation();
      dataLoaded = false;
      showCreatorSpotlightEditModal = false;
    }}
    on:close={() => {
      loadSpotlightInformation();
      dataLoaded = false;
      showCreatorSpotlightEditModal = false;
    }} />
{/if}

<div class="svelte-modal">
  <Modal
    mobile={mobile}
    on:close={() => dispatch('close')}
    show={showModal}
    showCloseButton={true}
    closeButtonClass="modal__close--custom">
    <div slot="content" class="content-container" class:mobile={mobile}>
      <div class="main-container">
        <div class="image-carousel">
          {#if user.id === currentUser.id}
            <button
              class="edit-button"
              on:click={() => {
                showCreatorSpotlightEditModal = true;
              }}
              ><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M0 9.49965V12H2.50035L9.87804 4.62231L7.37769 2.12196L0 9.49965ZM11.805 2.69537C12.065 2.43534 12.065 2.01195 11.805 1.75191L10.2481 0.195027C9.98806 -0.065009 9.56466 -0.065009 9.30463 0.195027L8.08446 1.4152L10.5848 3.91554L11.805 2.69537Z"
                  fill="#1A1A1A" />
              </svg>
            </button>
          {/if}

          {#if dataLoaded}
            <Carousel items={galleryItems} mobile={mobile} user={user}>
              <svelte:fragment let:item>
                <div>
                  <img class="spotlight-image" alt="spotlight gallery" src={item.content_url} />
                  <div class="image-subtle-overlay"></div>
                </div>
              </svelte:fragment>
            </Carousel>
          {:else}
            <PreloadBox style="width: 100%; max-width: 350px; min-height: 350px;" />
          {/if}
        </div>

        <div class="user-meta">
          {#if dataLoaded}
            <div>
              <AccountVerifiedIcon dimension={16} verified={user.verified} clickable={false}>
                <svelte:fragment slot="account-name">
                  <span class="name">
                    {user.name}
                  </span>
                </svelte:fragment>
              </AccountVerifiedIcon>
              <div class="username">@{user.username}</div>
            </div>

            {#if topThreeFollowers.length > 0}
              <div class="user-stack">
                <div class="user-stack-avatars">
                  {#each topThreeFollowers as follower}
                    <span class="follower">
                      <Avatar
                        src={follower.avatar}
                        user={follower}
                        width={24}
                        mobile={mobile}
                        allowImageUpload={false}
                        plusIcon={false}
                        href={follower.profile_url}
                        showSpotlightIfApplicable={false} />
                    </span>
                  {/each}
                </div>
                <div class="followers-text">
                  <span>
                    Followed by <a
                      href={topThreeFollowers[0].profile_url}
                      on:click={(e) => {
                        handleHrefLink(e, topThreeFollowers[0].profile_url);
                      }}
                      class="user-stack__link">{topThreeFollowers[0].name}</a>
                  </span>
                  {#if topThreeFollowers[1]}
                    <span>, </span><a
                      href={topThreeFollowers[1].profile_url}
                      on:click={(e) => {
                        handleHrefLink(e, topThreeFollowers[1].profile_url);
                      }}
                      class="user-stack__link">{topThreeFollowers[1].name}</a>
                  {/if}
                  {#if otherFollowersCount > 0}
                    <span
                      >, and <a
                        class="user-stack__link"
                        href={mobile ? `${user.profile_url + '/followers'}` : `${user.profile_url + '?followers=1'}`}
                        on:click={(e) => {
                          handleHrefLink(
                            e,
                            mobile ? `${user.profile_url + '/followers'}` : `${user.profile_url + '?followers=1'}`
                          );
                        }}>{otherFollowersCount} others</a
                      ></span>
                  {/if}
                </div>
              </div>
            {/if}

            {#if invitedBy}
              <div class="user-stack">
                <Avatar
                  src={invitedBy.avatar}
                  user={invitedBy}
                  width={24}
                  allowImageUpload={false}
                  plusIcon={false}
                  mobile={mobile}
                  showSpotlightIfApplicable={false} />
                <span
                  >Invited by <a
                    href={invitedBy.profile_url}
                    on:click={(e) => {
                      handleHrefLink(e, invitedBy.profile_url);
                    }}
                    class="user-stack__link">{invitedBy.name}</a
                  ></span>
              </div>
            {/if}
          {:else}
            <div class="preload-user-stack">
              <PreloadBox style="width: 100%; max-width: 350px; height: 50px" />
              <PreloadBox style="width: 100%; max-width: 350px; height: 24px" />
              <PreloadBox style="width: 100%; max-width: 350px; height: 24px" />
            </div>
          {/if}
        </div>
        <div class="user-actions">
          {#if dataLoaded}
            <UserFollowButton
              on:follow-botton-clicked={(e) => {
                const details = e.detail;
                Track.spotlightFollowClicked({
                  currentUser: currentUser,
                  user: user,
                  path: details.path,
                  action: details.action,
                });
              }}
              mobile={mobile}
              user={user}
              context="other_profile"
              size="full"
              colorScheme="white" />
            <Button
              size="full"
              colorScheme={'black'}
              style="white-space: nowrap; max-width: 50%;"
              on:click={(e) => {
                Track.spotlightViewProfileClicked({
                  currentUser: currentUser,
                  user: user,
                  path: location.pathname,
                });
                handleHrefLink(e, user.profile_url || user.profile_path);
              }}
              text={'Visit profile'} />
          {:else}
            <PreloadBox style="width: 100%; max-width: 350px; height: 66px" />
          {/if}
        </div>
      </div>
      <div style="overflow-y: auto;" class="purple-scrollbar-thin">
        <div class="side-container">
          {#if dataLoaded}
            {#if basicInformation.age || basicInformation.location || basicInformation.school || basicInformation.job_title}
              <div class="user-details-container">
                {#if basicInformation.location}
                  <div class="user-details">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M12.4084 4.62874C12.1632 4.45709 11.8368 4.45709 11.5916 4.62874L4.80345 9.38073C4.61326 9.51388 4.5 9.73136 4.5 9.96343V17.4309C4.5 18.0051 4.7541 18.5386 5.1773 18.9195C5.59803 19.2982 6.15413 19.5 6.72035 19.5H8.73729C8.79288 19.5 8.84699 19.4936 8.89892 19.4816C8.95083 19.4936 9.00492 19.5 9.06049 19.5C9.45365 19.5 9.77237 19.1815 9.77237 18.7886V12.0691H14.2275V18.7886C14.2275 19.1815 14.5463 19.5 14.9394 19.5C14.995 19.5 15.0491 19.4936 15.101 19.4816C15.153 19.4936 15.2071 19.5 15.2627 19.5H17.2797C17.8459 19.5 18.402 19.2982 18.8227 18.9195C19.2459 18.5386 19.5 18.0051 19.5 17.4309V9.96343C19.5 9.73136 19.3867 9.51388 19.1965 9.38073L12.4084 4.62874Z"
                        stroke="#7F7F7F"
                        stroke-width="1.5" />
                    </svg>
                    <div>Lives in {basicInformation.location}</div>
                  </div>
                {/if}
                {#if basicInformation.age}
                  <div class="user-details">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M10.5302 4.87772C10.8254 4.34567 11.3811 4 12.0016 4C12.622 4 13.1778 4.34567 13.473 4.87772L13.4852 4.89968L13.4959 4.9224L15.0719 8.27332L18.5906 8.81013L18.5909 8.81019C19.2306 8.90811 19.7376 9.37245 19.9246 9.97535C20.1107 10.5754 19.9595 11.2406 19.5158 11.6945L19.5151 11.6952L16.949 14.3127L17.5556 18.0199L17.5556 18.02C17.657 18.6399 17.4168 19.2799 16.909 19.6663C16.3957 20.0569 15.7061 20.111 15.1388 19.7987C15.1388 19.7986 15.1387 19.7986 15.1387 19.7986L12.0017 18.0716L8.86267 19.8005C8.29537 20.1129 7.60569 20.0588 7.09236 19.6682C6.58458 19.2818 6.34436 18.6418 6.44575 18.0219L6.44576 18.0218L7.05236 14.3143L4.48786 11.6949C4.48783 11.6949 4.48779 11.6949 4.48776 11.6948C4.04297 11.2406 3.89148 10.5742 4.07843 9.97335C4.26618 9.36985 4.77461 8.90564 5.41528 8.80905C5.41555 8.80901 5.41583 8.80897 5.4161 8.80892L8.93126 8.2741L10.5073 4.92247L10.518 4.89971L10.5302 4.87772ZM11.8505 5.59098L10.1184 9.27452L9.94812 9.63665L9.5525 9.69684L5.64034 10.2921L5.63896 10.2923C5.5965 10.2987 5.53684 10.3349 5.51071 10.4189C5.4837 10.5058 5.50986 10.5947 5.55948 10.6454L5.55959 10.6455L8.38972 13.5362L8.65525 13.8074L8.59396 14.1819L7.92608 18.264C7.92608 18.264 7.92608 18.264 7.92608 18.264C7.91099 18.3564 7.95035 18.4362 8.0007 18.4745C8.0455 18.5086 8.09573 18.5104 8.13902 18.4866L8.50084 19.1435L8.13902 18.4866L11.6398 16.5585L12.0015 16.3593L12.3633 16.5584L15.8622 18.4846L15.8623 18.4847C15.9056 18.5085 15.9558 18.5067 16.0006 18.4726C16.051 18.4343 16.0904 18.3545 16.0753 18.2621L15.4074 14.18L15.346 13.8052L15.612 13.5339L18.4432 10.6458C18.4433 10.6457 18.4434 10.6457 18.4435 10.6456C18.4928 10.5949 18.5188 10.5063 18.4919 10.4197C18.4659 10.3359 18.4066 10.2995 18.3641 10.2929C18.3641 10.2929 18.364 10.2929 18.3639 10.2929L14.4504 9.69585L14.055 9.63553L13.8848 9.27363L12.1527 5.591C12.1103 5.52519 12.0498 5.5 12.0016 5.5C11.9533 5.5 11.8929 5.52518 11.8505 5.59098Z"
                        fill="#7F7F7F" />
                    </svg>
                    <div>{basicInformation.age} {basicInformation.age == 1 ? 'year old' : 'years old'}</div>
                  </div>
                {/if}
                {#if basicInformation.job_title}
                  <div class="user-details">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clip-path="url(#clip0_1_3177)">
                        <path
                          d="M9 18.7485V8.24967C9 7.55227 9 7.20357 9.07667 6.91748C9.28472 6.1411 9.8912 5.53469 10.6677 5.32666C10.9538 5.25 11.3025 5.25 12 5.25C12.6975 5.25 13.0462 5.25 13.3323 5.32666C14.1088 5.53469 14.7153 6.1411 14.9233 6.91748C15 7.20357 15 7.55227 15 8.24967V18.7485M6.9 18.7485H17.1C17.9401 18.7485 18.3601 18.7485 18.681 18.5851C18.9632 18.4413 19.1927 18.2118 19.3365 17.9296C19.5 17.6088 19.5 17.1888 19.5 16.3488V10.6494C19.5 9.80943 19.5 9.38943 19.3365 9.0686C19.1927 8.78639 18.9632 8.55694 18.681 8.41315C18.3601 8.24967 17.9401 8.24967 17.1 8.24967H6.9C6.05992 8.24967 5.63988 8.24967 5.31901 8.41315C5.03677 8.55694 4.8073 8.78639 4.66349 9.0686C4.5 9.38943 4.5 9.80943 4.5 10.6494V16.3488C4.5 17.1888 4.5 17.6088 4.66349 17.9296C4.8073 18.2118 5.03677 18.4413 5.31901 18.5851C5.63988 18.7485 6.05992 18.7485 6.9 18.7485Z"
                          stroke="#7F7F7F"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_1_3177">
                          <rect width="16" height="16" fill="white" transform="translate(4 4)" />
                        </clipPath>
                      </defs>
                    </svg>

                    <div>{basicInformation.job_title}</div>
                  </div>
                {/if}
                {#if basicInformation.school}
                  <div class="user-details">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clip-path="url(#clip0_1_1838)">
                        <path
                          d="M15.75 13.8555V11.601C15.75 11.4663 15.75 11.399 15.7295 11.3396C15.7114 11.287 15.6818 11.2392 15.6429 11.1994C15.5989 11.1545 15.5387 11.1244 15.4183 11.0642L12 9.35477M6 10.1049V15.2107C6 15.4896 6 15.6291 6.04351 15.7513C6.08198 15.8592 6.14468 15.957 6.22678 16.0369C6.31966 16.1274 6.44645 16.1855 6.70001 16.3017L11.5 18.5021C11.6839 18.5864 11.7759 18.6285 11.8717 18.6452C11.9566 18.6599 12.0434 18.6599 12.1283 18.6452C12.2241 18.6285 12.3161 18.5864 12.5 18.5021L17.3 16.3017C17.5535 16.1855 17.6803 16.1274 17.7732 16.0369C17.8553 15.957 17.918 15.8592 17.9565 15.7513C18 15.6291 18 15.4897 18 15.2107V10.1049M4.5 9.35477L11.7317 5.73835C11.8301 5.68915 11.8793 5.66455 11.9309 5.65487C11.9766 5.64629 12.0234 5.64629 12.0691 5.65487C12.1207 5.66455 12.1699 5.68915 12.2683 5.73835L19.5 9.35477L12.2683 12.9712C12.1699 13.0204 12.1207 13.045 12.0691 13.0547C12.0234 13.0632 11.9766 13.0632 11.9309 13.0547C11.8793 13.045 11.8301 13.0204 11.7317 12.9712L4.5 9.35477Z"
                          stroke="#7F7F7F"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_1_1838">
                          <rect width="16" height="16" fill="white" transform="translate(4 4)" />
                        </clipPath>
                      </defs>
                    </svg>
                    <div>{basicInformation.school}</div>
                  </div>
                {/if}
              </div>
            {/if}
            {#if user.bio || (socialProfiles && socialProfiles.length > 0)}
              <div>
                {#if user.bio}
                  <div class="spotlight-heading">About me</div>
                  <Text style="color: #7f7f7f;" text={user.bio} editable={false} lines={10} />
                {/if}
                {#if socialProfiles && socialProfiles.length > 0}
                  <div class="social-icons">
                    {#each socialProfiles as sl}
                      <a
                        target="_blank"
                        data-tippy-content={sl.label}
                        href={sl.url}
                        on:click={(event) => {
                          event.preventDefault();
                          Track.spotlightSocialLinkClicked({
                            currentUser: currentUser,
                            user: user,
                            path: location.pathname,
                            linkDomain: sl.link_type,
                            link: sl.url,
                          });
                          window.open(sl.url);
                        }}
                        rel="nofollow"
                        style="margin-right: 12px; width: 24px; height: 24px;">
                        <img src={`/images/profile/socials/${sl.link_type}.svg`} style="width: 24px; height: 24px;" />
                      </a>
                    {/each}
                  </div>
                {/if}
              </div>
            {/if}

            {#if expertises && expertises.length > 0}
              <div>
                <div class="spotlight-heading">Ask me for recs about</div>
                <div class="pill-container">
                  {#each expertises as entry}
                    <span class="pill">{entry.value}</span>
                  {/each}
                </div>
              </div>
            {/if}

            {#if questions && questions.length > 0}
              <div>
                <div class="spotlight-heading">Answers</div>
                <div class="answers-container">
                  {#each questions as entry}
                    <div class="question-container">
                      <div class="question-item">{entry.value}</div>
                      <div class="answer-item">{entry.answer}</div>
                    </div>
                  {/each}
                </div>
              </div>
            {/if}

            {#if brands && brands.length > 0}
              <div>
                <div class="spotlight-heading">Favorite brands</div>
                <div class="pill-container">
                  {#each brands as entry}
                    <span class="pill">{entry.value}</span>
                  {/each}
                </div>
              </div>
            {/if}

            {#if values && values.length > 0}
              <div>
                <div class="spotlight-heading">Recommendation Values</div>
                <div class="pill-container">
                  {#each values as entry}
                    <span class="pill">{entry.value}</span>
                  {/each}
                </div>
              </div>
            {/if}

            {#if interests && interests.length > 0}
              <div>
                <div class="spotlight-heading">Interests</div>
                <div class="pill-container">
                  {#each interests as entry}
                    <span class="pill">{entry.value}</span>
                  {/each}
                </div>
              </div>
            {/if}

            {#if pinnedLists && pinnedLists.length > 0}
              <div>
                <div class="spotlight-heading">Pinned lists</div>
                <div class="pinned-list-container">
                  {#each pinnedLists as group}
                    <div data-group-id={group.id} class="group-container">
                      <PinnedList
                        mobile={mobile}
                        id={group.id}
                        showDetailsButton={false}
                        showWelcomeNoteFlag={false}
                        user={user}
                        on:tile-clicked={(e) => {
                          if (e.detail.event && (e.detail.event.ctrlKey || e.detail.event.metaKey)) {
                            window.open(group.path);
                          } else {
                            location.href = group.path;
                          }
                        }} />
                    </div>
                  {/each}
                </div>
              </div>
            {/if}
          {/if}
        </div>
      </div>
    </div>
  </Modal>
</div>
